import { z } from "zod";

import { APP_VIEWS, appViewSchema } from "./views";

/**
 * Effective role of a user in the active organization.
 *
 * SUPER_ADMIN is platform-wide and does not come from a membership: it is
 * derived from the user record (ADR-016). The API exposes the three roles
 * uniformly so clients do not need to know that distinction.
 */
export const ROLES = ["USER", "ADMIN", "SUPER_ADMIN"] as const;

export const roleSchema = z.enum(ROLES);
export type Role = z.infer<typeof roleSchema>;

/** Transactional email and UI locales (ADR-076 / ADR-085). */
export const APP_LOCALES = ["en-GB", "es-AR"] as const;
export const appLocaleSchema = z.enum(APP_LOCALES);
export type AppLocale = z.infer<typeof appLocaleSchema>;
export const DEFAULT_APP_LOCALE: AppLocale = "es-AR";

export const loginRequestSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(254),
  password: z.string().min(1).max(256),
});

export type LoginRequest = z.infer<typeof loginRequestSchema>;

/**
 * Always succeeds with the same body whether or not the email is known
 * (ADR-085). Locale drives the mail copy and is stored as preferredLocale
 * when a matching user exists.
 */
export const passwordResetRequestSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(254),
  locale: appLocaleSchema.optional(),
});

export type PasswordResetRequest = z.infer<typeof passwordResetRequestSchema>;

export const passwordResetRequestResponseSchema = z.object({
  ok: z.literal(true),
});

export type PasswordResetRequestResponse = z.infer<typeof passwordResetRequestResponseSchema>;

/** New password for magic-link confirm (reset or invite). */
export const newPasswordSchema = z
  .string()
  .min(12, "Use at least 12 characters.")
  .max(256);

export const passwordResetConfirmSchema = z.object({
  token: z.string().min(20).max(512),
  password: newPasswordSchema,
});

export type PasswordResetConfirm = z.infer<typeof passwordResetConfirmSchema>;

export const passwordResetConfirmResponseSchema = z.object({
  ok: z.literal(true),
});

export type PasswordResetConfirmResponse = z.infer<typeof passwordResetConfirmResponseSchema>;

export const authUserSchema = z.object({
  id: z.uuid(),
  email: z.string().email(),
  name: z.string(),
  role: roleSchema,
  organizationId: z.uuid().nullable(),
  organizationName: z.string().nullable(),
  /**
   * Modules this session may open (ADR-088). SUPER_ADMIN receives the full
   * catalogue; members receive their membership allowlist.
   */
  views: z.array(appViewSchema),
});

export type AuthUser = z.infer<typeof authUserSchema>;

/** Full catalogue used when a platform admin has no membership row. */
export const SUPER_ADMIN_VIEWS = [...APP_VIEWS] as const;

/**
 * The refresh token is deliberately absent: it travels only in an httpOnly
 * cookie and must never be reachable from JavaScript (section 6).
 */
export const loginResponseSchema = z.object({
  accessToken: z.string(),
  /** Access token lifetime in seconds, so the client can refresh ahead of expiry. */
  expiresIn: z.number().int().positive(),
  user: authUserSchema,
});

export type LoginResponse = z.infer<typeof loginResponseSchema>;

export const refreshResponseSchema = loginResponseSchema;
export type RefreshResponse = z.infer<typeof refreshResponseSchema>;

export const meResponseSchema = z.object({ user: authUserSchema });
export type MeResponse = z.infer<typeof meResponseSchema>;

export const logoutResponseSchema = z.object({
  /** Sessions closed. 1 for logout, N for logout-all. */
  revokedSessions: z.number().int().nonnegative(),
});

export type LogoutResponse = z.infer<typeof logoutResponseSchema>;
